import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { eventService } from '../services/api';
import API_BASE_URL from '../config';
import '../styles/EventPage.css';

const EventPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFullImage, setShowFullImage] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copyNotification, setCopyNotification] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    try {
      const { data } = await eventService.getEventById(id);
      setEvent(data);
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCalendar = () => {
    if (!event) return;

    const startDateTime = new Date(`${event.startDate.split('T')[0]}T${event.startTime}`);
    const endDateTime = new Date(`${event.endDate.split('T')[0]}T${event.endTime}`);

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${formatDateForICS(startDateTime)}
DTEND:${formatDateForICS(endDateTime)}
SUMMARY:${event.title}
DESCRIPTION:${event.description}
LOCATION:${event.location}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.title}.ics`;
    link.click();
  };

  const formatDateForICS = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const handleShare = () => {
    const url = window.location.href;
    const title = event ? event.title : 'WennWieWo Event';

    if (navigator.share) {
      navigator.share({
        title: title,
        text: `Schau dir dieses Event an: ${title}`,
        url: url
      }).catch(() => {
        setShowShareMenu(!showShareMenu);
      });
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  const shareVia = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(event ? event.title : 'WennWieWo Event');

    const shareUrls = {
      whatsapp: `https://wa.me/?text=${title}%20${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      email: `mailto:?subject=${title}&body=${title}%20${url}`,
      copy: url
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(window.location.href);
      setCopyNotification(true);
      setShowShareMenu(false);
      setTimeout(() => setCopyNotification(false), 2000);
    } else {
      window.open(shareUrls[platform], '_blank');
      setShowShareMenu(false);
    }
  };

  if (loading) return <div className="loading">Event wird geladen...</div>;
  if (!event) return <div className="error">Event nicht gefunden</div>;

  return (
    <div className="event-page">
      {event.bannerImage && (
        <div className="event-banner">
          <img
            src={`${API_BASE_URL}${event.bannerImage}`}
            alt=""
            onClick={() => setShowFullImage(true)}
            style={{ cursor: 'pointer' }}
            title="Klicken zum Vergrössern"
          />
          <button
            className="btn-view-fullsize"
            onClick={() => setShowFullImage(true)}
            title="Bild in voller Grösse anzeigen"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 3 21 3 21 9"></polyline>
              <polyline points="9 21 3 21 3 15"></polyline>
              <line x1="21" y1="3" x2="14" y2="10"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>
          </button>
        </div>
      )}

      {showFullImage && (
        <div className="image-lightbox" onClick={() => setShowFullImage(false)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-lightbox" onClick={() => setShowFullImage(false)} title="Schliessen">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <img src={`${API_BASE_URL}${event.bannerImage}`} alt={event.title} />
          </div>
        </div>
      )}

      <div className="event-content">
        {event.status === 'pending' && (
          <div className="status-tag--pending">
            <strong>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              Genehmigung ausstehend
            </strong>
          </div>
        )}
        {event.status === 'rejected' && (
          <div className="rejection-banner">
            <strong>Dieses Event wurde abgelehnt.</strong>
            {event.rejectionReason && (
              <p>Begründung: {event.rejectionReason}</p>
            )}
            {user && event.organizer && user._id === event.organizer._id && (
              <Link to={`/edit/${event._id}`} className="btn btn-primary" style={{ marginTop: '12px', textDecoration: 'none', alignSelf: 'flex-start' }}>
                Event überarbeiten
              </Link>
            )}
          </div>
        )}
        <h1>{event.title}</h1>

        <div className="event-info">
          <div className="info-item">
            <span className="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </span>
            <span className="info-label">Beginn:</span>
            <span className="info-value">{new Date(event.startDate).toLocaleDateString('de-CH')} {event.startTime}</span>
          </div>
          <div className="info-item">
            <span className="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </span>
            <span className="info-label">Ende:</span>
            <span className="info-value">{new Date(event.endDate).toLocaleDateString('de-CH')} {event.endTime}</span>
          </div>
          <div className="info-item">
            <span className="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </span>
            <span className="info-label">Ort:</span>
            <span className="info-value">{event.location}, {event.canton}</span>
          </div>
          <div className="info-item">
            <span className="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </span>
            <span className="info-label">Preis:</span>
            <span className="info-value">{event.price}</span>
          </div>
          {event.minimumAge > 0 && (
            <div className="info-item">
              <span className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </span>
              <span className="info-label">Mindestalter:</span>
              <span className="info-value">{event.minimumAge}+</span>
            </div>
          )}
          <div className="info-item full-width action-buttons">
            {event.website && (
              <a href={event.website} target="_blank" rel="noopener noreferrer" className="btn-more-info">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
                Mehr Informationen
              </a>
            )}
            <button onClick={addToCalendar} className="btn-action-icon" title="Zum Kalender hinzufügen">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
                <line x1="12" y1="14" x2="12" y2="18"></line>
                <line x1="10" y1="16" x2="14" y2="16"></line>
              </svg>
              <span className="action-label">Kalender</span>
            </button>
            <div className="share-container">
              <button onClick={handleShare} className="btn-action-icon" title="Teilen">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
                <span className="action-label">Teilen</span>
              </button>
              {showShareMenu && (
                <div className="share-menu">
                  <button onClick={() => shareVia('whatsapp')} className="share-option">
                    <span>WhatsApp</span>
                  </button>
                  <button onClick={() => shareVia('facebook')} className="share-option">
                    <span>Facebook</span>
                  </button>
                  <button onClick={() => shareVia('twitter')} className="share-option">
                    <span>Twitter</span>
                  </button>
                  <button onClick={() => shareVia('email')} className="share-option">
                    <span>E-Mail</span>
                  </button>
                  <button onClick={() => shareVia('copy')} className="share-option">
                    <span>Link kopieren</span>
                  </button>
                </div>
              )}
              {copyNotification && (
                <div className="copy-notification">Link kopiert!</div>
              )}
            </div>
          </div>
        </div>

        <div className="event-description">
          <h2>Beschreibung</h2>
          <p>{event.description}</p>
        </div>

        {event.organizer && (
          <div className="event-organizer">
            <h2>Veranstalter</h2>
            <Link to={`/organizer/${event.organizer._id}`} className="organizer-link">
              {event.organizer.isVerified && event.organizer.organizerName
                ? event.organizer.organizerName
                : (event.organizer.profileName || event.organizer.organizerName)}
              {event.organizer.isVerified && (
                <span className="verified-badge" title="Verifizierter Veranstalter">✓</span>
              )}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventPage;
